import {yupResolver} from "@hookform/resolvers/yup";
import {format} from "date-fns";
import React, {useEffect} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {EmailListField} from "@/components/form/fields/EmailListField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {UserSelectField} from "@/components/form/fields/UserSelectField/UserSelectField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {SupportLink} from "@/components/formSubtext/subComponents/SupportLink/SupportLink.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {timezoneAbbr} from "@/lib/format.tsx";
import {
  ScheduledReport,
  ScheduledReportFileType,
  ScheduledReportRecurrence,
  SLAReport,
  StandardDateRange,
} from "@/lib/queries/reports.ts";
import {AccountUser} from "@/lib/types/accounts.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {StandardDateRangeLabels} from "@/lib/types/reports.ts";
import {
  ScheduledReportFileTypeLabel,
  ScheduledReportRangeOverrideOptions,
  ScheduledReportRecurrenceLabel,
} from "@/pages/reports/schedule/types.ts";

interface ScheduledReportFormValues {
  id?: number;
  name: string;
  report: string;
  file_type: ScheduledReportFileType;
  recurrence: ScheduledReportRecurrence;
  default_date_range_override?: StandardDateRange | null;
  at_time: number;
  on_weekday: string;
  recipient_emails: string[];
  recipient_users: number[];
  is_enabled: boolean;
}

const ScheduledReportSchema = Yup.object().shape(
  {
    id: Yup.number().optional(),
    name: Yup.string().required("This field is required.").max(100),
    report: Yup.string().required("This field is required."),
    file_type: Yup.mixed<ScheduledReportFileType>()
      .oneOf(Object.values(ScheduledReportFileType))
      .required("This field is required."),
    recurrence: Yup.mixed<ScheduledReportRecurrence>()
      .oneOf(Object.values(ScheduledReportRecurrence))
      .required("This field is required."),
    default_date_range_override: Yup.mixed<StandardDateRange>()
      .oneOf(Object.values(StandardDateRange))
      .nullable()
      .default(null),
    at_time: Yup.number().required("This field is required."),
    on_weekday: Yup.string()
      .oneOf([...Array(8).keys()].slice(1).map((i) => i.toString()))
      .required("This field is required"),
    recipient_emails: Yup.array()
      .defined()
      .of(Yup.string().email().defined())
      .when("recipient_users", {
        is: (val: number[]) => !val || val.length === 0,
        then: (schema) => schema.min(1, "At least one email address is required."),
      }),
    recipient_users: Yup.array()
      .defined()
      .of(Yup.number().defined())
      .when("recipient_emails", {
        is: (val: string[]) => val?.length === 0,
        then: (schema) => schema.min(1, "At least one user is required."),
      }),
    is_enabled: Yup.boolean().default(false),
  },
  [
    ["recipient_emails", "recipient_users"],
    ["recipient_users", "recipient_emails"],
  ]
);

export interface ScheduledReportDefaultValues extends Omit<ScheduledReport, "id"> {
  id?: number;
}

interface ScheduledReportFormProps {
  formId: string;
  slaReports: SLAReport[];
  users: AccountUser[];
  onSubmit: (data: ScheduledReportFormValues) => void;
  onError?: (errors: FieldErrors<ScheduledReportFormValues>, event?: React.BaseSyntheticEvent) => void;
  serverErrors?: ServerErrors;
  defaultValues?: ScheduledReportDefaultValues;
}

export const ScheduledReportForm = ({
  formId,
  users,
  onSubmit,
  onError,
  slaReports,
  serverErrors,
  defaultValues,
}: ScheduledReportFormProps) => {
  const {user} = useCurrentUserContext();
  const methods = useForm<ScheduledReportFormValues>({
    resolver: yupResolver<ScheduledReportFormValues>(ScheduledReportSchema),
    defaultValues: {
      id: defaultValues?.id,
      name: defaultValues?.name || "",
      file_type: defaultValues?.file_type || ScheduledReportFileType.PDF,
      recurrence: defaultValues?.recurrence || ScheduledReportRecurrence.WEEKLY,
      default_date_range_override: defaultValues?.default_date_range_override || null,
      at_time: defaultValues?.at_time ?? 11,
      on_weekday: defaultValues?.on_weekday.toString() || "1",
      recipient_emails: defaultValues?.recipient_emails || [],
      recipient_users: defaultValues?.recipient_users || [],
      is_enabled: defaultValues ? defaultValues.is_enabled : true,
      report: defaultValues?.report?.key || "",
    },
  });
  const reportOptions: SelectOption[] = slaReports.map((report) => ({
    label: report.name,
    value: report.key,
  }));
  const timeOptions = [
    ...[...Array(24).keys()].map((k) => ({
      value: k,
      label: format(new Date().setHours(k, 0, 0, 0), "hh:mm a"),
    })),
    // Also put 12:00 AM at the end for convenience
    ...[
      {
        value: 0,
        label: format(new Date().setHours(0, 0, 0, 0), "hh:mm a"),
      },
    ],
  ];
  const {handleSubmit, watch, register, setError} = methods;
  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, messages]) => {
        if (field !== "__all__") {
          setError(field as keyof ScheduledReportFormValues, {
            message: Array.isArray(messages) ? messages.join("\n") : String(messages),
          });
        }
      });
    }
  }, [serverErrors, setError]);

  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={handleSubmit(onSubmit, onError)}>
        {serverErrors?.__all__ && (
          <div className="mb-3">
            <FormErrorAlerts errors={serverErrors?.__all__} />
          </div>
        )}
        <div className="pulseTextBody pulse-color-gray-800 mb-4">
          Scheduling the SLA Report allows your organization to deliver periodic reports on specific Checks or systems
          to users or specific email addresses.
          <SupportLink
            href="https://support.uptime.com/hc/en-us/articles/360006291539-Uptime-com-Reporting#scheduling-reports"
            openInZendesk={false}
            className="ms-1"
          />
        </div>
        {defaultValues?.id && <input type="number" hidden {...register("id")} />}
        <div className="row mb-3">
          <InputField className="col" name="name" label="Schedule Name" required />
        </div>
        <div className="row mb-3">
          <SelectField
            className="col"
            options={reportOptions}
            name="report"
            label="SLA Report to Send"
            placeholder="Select a report..."
            required
          />
          <SelectField
            className="col-6"
            name="file_type"
            label="File Type"
            options={Object.keys(ScheduledReportFileTypeLabel).map((key) => ({
              value: key,
              label: ScheduledReportFileTypeLabel[key as ScheduledReportFileType] as string,
            }))}
            required
          />
        </div>
        <div className="row mb-3">
          <SelectField
            className="col"
            name="recurrence"
            label="Recurrence"
            tooltip="How often to deliver this report. Monthly, Quarterly and Yearly reports are sent on the 1st of the appropriate month(s)."
            options={Object.keys(ScheduledReportRecurrenceLabel).map((key) => ({
              value: key,
              label: ScheduledReportRecurrenceLabel[key as ScheduledReportRecurrence] as string,
            }))}
            required
          />
          <SelectField
            className="col"
            name="default_date_range_override"
            label="Override Report Date Range"
            tooltip="Override SLA Report default date range."
            options={[{label: "Do Not Override", value: null} as SelectOption<string | null>].concat(
              ScheduledReportRangeOverrideOptions.map((key) => ({
                value: key,
                label: StandardDateRangeLabels[key] as string,
              }))
            )}
          />
        </div>
        <div className="row mb-3">
          <SelectField
            className="col"
            options={timeOptions}
            name="at_time"
            label={`At Time ${timezoneAbbr(user!.account.timezone)}`}
            tooltip="Deliver the report at this time (local time)."
            required
          />
          <SelectField
            className="col"
            options={[
              {value: "1", label: "Monday"},
              {value: "2", label: "Tuesday"},
              {value: "3", label: "Wednesday"},
              {value: "4", label: "Thursday"},
              {value: "5", label: "Friday"},
              {value: "6", label: "Saturday"},
              {value: "7", label: "Sunday"},
            ]}
            name="on_weekday"
            label="On Day"
            tooltip="Weekly reports will be sent on this day."
            disabled={watch("recurrence") !== ScheduledReportRecurrence.WEEKLY}
            required
          />
        </div>
        <div className="row mb-3">
          <UserSelectField
            className="col-6"
            tooltip="Users in your account that will receive these emailed reports."
            name="recipient_users"
            label="Send to Users"
            users={users}
            isMulti
          />
          <div className="col-6">
            <EmailListField
              tooltip="Additional email addresses that will receive this report."
              name="recipient_emails"
              label="Send to Emails"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <SwitchField name="is_enabled" label="Schedule enabled" />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
